<script setup>

import SlideoutWithSlots from "../elements/slideoutWithSlots.vue";
import {computed, ref} from "vue";
import {_mft} from "../../i18n.js";
import { useNotificationStore } from '@/stores/NotificationStore.js';

const emit = defineEmits(['reload', 'deleted', 'deselect'])

const props = defineProps({
    leads: {
        type: Array,
        required: false,
        default: () => []
    },
    canArchive: {
        type: Boolean,
        default: false
    },
})

// Mirrors LeadArchiveController::MAX_BULK_ARCHIVE.
const MAX_BULK_ARCHIVE = 25

const notificationStore = useNotificationStore()
const archiving = ref(false)

const tooManyToArchive = computed(() => props.leads.length > MAX_BULK_ARCHIVE)

const tagSlideoutOpen = ref(false)
const tagDetachSlideoutOpen = ref(false)
const deleteSlideoutOpen = ref(false)

const modalClass = computed(() => {
    if (tagSlideoutOpen.value || tagDetachSlideoutOpen.value || deleteSlideoutOpen.value) {
        return 'top-8 translate-y-0 opacity-0'
    }
    return props.leads.length > 0 ? 'top-8 translate-y-0' : 'top-0 -mt-48 -translate-y-full'
})

const onTagsDetached = () => {
    tagDetachSlideoutOpen.value = false
    emit('reload')
}

const onDeleted = () => {
    deleteSlideoutOpen.value = false
    emit('deleted')
}

const archiveSelected = async () => {
    if (archiving.value || props.leads.length === 0 || tooManyToArchive.value) {
        return
    }

    archiving.value = true

    try {
        const res = await axios.post(route('api.crm.leads.archive'), {
            lead_ids: props.leads.map(lead => lead.id),
        })

        const skipped = res.data.already_in_state + res.data.rejected
        let message = _mft('crm:archive.result.archived', {count: res.data.succeeded})
        if (skipped > 0) {
            message += '. ' + _mft('crm:archive.result.skipped', {count: skipped})
        }
        notificationStore.addNotification({
            type: 'success',
            title: message,
            dismissAfter: 5000,
        })
        emit('reload')
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:archive.error'),
            dismissAfter: 5000,
        })
    } finally {
        archiving.value = false
    }
}

</script>

<template>
    <div>
        <div
            class="fixed transition-all left-1/2 -translate-x-1/2 z-modal"
            :class="modalClass"
        >
            <div class="bg-white shadow-2xl rounded-wl p-2 pl-3">
                <div class="flex justify-center items-center space-x-2 mb-2">
                    <p class="text-center text-xs mb-0">
                        {{ _mft('crm:leadsMultiManage.selected', {count: leads.length}) }}
                    </p>
                    <button-secondary
                        size="xs"
                        @click="emit('deselect')"
                    >
                        {{ _mft('crm:leadsMultiManage.deselect') }}
                    </button-secondary>
                </div>
                <div
                    class="grid gap-4"
                    :class="canArchive ? 'grid-cols-4' : 'grid-cols-3'"
                >
                    <button-secondary
                        size="sm"
                        @click="tagSlideoutOpen = true"
                        class="w-full justify-center"
                    >
                        {{ _mft('crm:leadsMultiManage.addTags') }}
                    </button-secondary>
                    <button-secondary
                        size="sm"
                        @click="tagDetachSlideoutOpen = true"
                        class="w-full justify-center"
                    >
                        {{ _mft('crm:leadsMultiManage.removeTag') }}
                    </button-secondary>
                    <template v-if="canArchive">
                        <!-- button-with-confirmation has no disabled state, so render a plain disabled button over the limit -->
                        <button-base
                            v-if="tooManyToArchive"
                            style-type="disabled"
                            size="sm"
                            :disabled="true"
                            class="w-full justify-center"
                        >
                            {{ _mft('crm:archive.archive') }}
                        </button-base>
                        <button-with-confirmation
                            v-else
                            style-type="secondary"
                            confirm-type="primary"
                            size="sm"
                            :state="archiving ? 'waiting' : ''"
                            :confirm-text="_mft('crm:archive.confirm.bulk', {count: leads.length})"
                            :confirm-button-label="_mft('crm:archive.archive')"
                            modal-class="absolute -top-2 -right-2 w-64 p-2 bg-white rounded shadow-md text-black z-modal"
                            class="grid [&>button]:justify-center"
                            @confirmed="archiveSelected"
                        >
                            {{ _mft('crm:archive.archive') }}
                        </button-with-confirmation>
                    </template>
                    <button-danger
                        size="sm"
                        @click="deleteSlideoutOpen = true"
                        class="w-full justify-center"
                    >
                        {{ _mft('crm:leadsMultiManage.deleteLeads') }}
                    </button-danger>
                </div>
                <p
                    v-if="canArchive && tooManyToArchive"
                    class="mt-2 mb-0 px-2 py-1 text-xs text-center text-amber-900 bg-amber-50 border border-amber-200 rounded-md"
                >
                    <i class="mr-1 fa-regular fa-triangle-exclamation"></i>
                    {{ _mft('crm:archive.bulkLimit', {max: MAX_BULK_ARCHIVE}) }}
                </p>
            </div>

            <slideout-with-slots
                v-if="tagSlideoutOpen"
                @close="tagSlideoutOpen = false"
            >
                <template #title>
                    <h2>{{ _mft('crm:leadsMultiManage.manageTitle', {count: leads.length}) }}</h2>
                </template>
                <template #body>
                    <div>
                        <tags-multi-assign-v2
                            :showTopbar="false"
                            :selected-leads="leads.map(lead => lead.id)"
                            @close="tagSlideoutOpen = false"
                        />
                    </div>
                </template>
            </slideout-with-slots>

            <slideout-with-slots
                v-if="tagDetachSlideoutOpen"
                @close="tagDetachSlideoutOpen = false"
            >
                <template #title>
                    <h2>{{ _mft('crm:leadsMultiManage.removeTagTitle', {count: leads.length}) }}</h2>
                </template>
                <template #body>
                    <div>
                        <tags-multi-detach-v2
                            :showTopbar="false"
                            :selected-leads="leads.map(lead => lead.id)"
                            @doReloadLeads="onTagsDetached"
                        />
                    </div>
                </template>
            </slideout-with-slots>

            <slideout-with-slots
                v-if="deleteSlideoutOpen"
                @close="deleteSlideoutOpen = false"
            >
                <template #title>
                    <h2>{{ _mft('crm:leadsMultiManage.deleteTitle', {count: leads.length}) }}</h2>
                </template>
                <template #body>
                    <div>
                        <tags-multi-delete
                            :selected-leads="leads"
                            @deleted="onDeleted"
                        />
                    </div>
                </template>
            </slideout-with-slots>

        </div>
    </div>
</template>
